/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import { extendTheme, ThemeProvider as Provider } from "@mui/joy/styles";
import { createElement, ReactNode } from "react";

/**
 * Customized Joy UI theme.
 * @see https://mui.com/joy-ui/customization/approaches/
 */
export const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        // Primary colors
        primary: {
          solidBg: "#7e41a3",
          solidHoverBg: "#663485",
          solidActiveBg: "#4f2867",
          softBg: "rgba(126, 65, 163, 0.1)",
          softColor: "#7e41a3",
          softHoverBg: "rgba(126, 65, 163, 0.2)",
        },
        // Neutral colors
        neutral: {
          50: "#fcfcfc",
          100: "#f4f4f4",
          200: "#dedede",
          300: "#cdcdcd",
          400: "#9f9f9f",
          500: "#646464",
          600: "#5e5e5e",
          700: "#383838",
          800: "#1a1a1a",
          900: "#0d0d0d",
          solidBg: "var(--joy-palette-neutral-500)",
          solidActiveBg: "var(--joy-palette-neutral-700)",
          softBg: "var(--joy-palette-neutral-100)",
          softHoverBg: "var(--joy-palette-neutral-200)",
          softActiveBg: "var(--joy-palette-neutral-300)",
          softColor: "var(--joy-palette-neutral-700)",
          softDisabledColor: "var(--joy-palette-neutral-400)",
          softDisabledBg: "var(--joy-palette-neutral-50)",
        },
        background: {
          body: "var(--joy-palette-neutral-50)",
          level1: "var(--joy-palette-neutral-100)",
          level2: "var(--joy-palette-neutral-200)",
          level3: "var(--joy-palette-neutral-300)",
          sidebarBg: "#fcfbfd",
        },
        text: {
          brandTitle: "#2a1537",
        },
      },
    },
    dark: {
      palette: {
        // Primary colors (same as light)
        primary: {
          solidBg: "#7e41a3",
          solidHoverBg: "#663485",
          solidActiveBg: "#4f2867",
          softBg: "rgba(126, 65, 163, 0.1)",
          softColor: "#b784d4",
          softHoverBg: "rgba(126, 65, 163, 0.2)",
        },
        // Neutral colors
        neutral: {
          50: "#fcfcfc",
          100: "#f4f4f4",
          200: "#dedede",
          300: "#cdcdcd",
          400: "#9f9f9f",
          500: "#646464",
          600: "#5e5e5e",
          700: "#383838",
          800: "#1a1a1a",
          900: "#0d0d0d",
          solidBg: "var(--joy-palette-neutral-500)",
          solidActiveBg: "var(--joy-palette-neutral-700)",
          softBg: "var(--joy-palette-neutral-800)",
          softHoverBg: "var(--joy-palette-neutral-700)",
          softActiveBg: "var(--joy-palette-neutral-600)",
          softColor: "var(--joy-palette-neutral-200)",
          softDisabledColor: "var(--joy-palette-neutral-500)",
          softDisabledBg: "var(--joy-palette-neutral-800)",
        },
        background: {
          body: "var(--joy-palette-neutral-900)",
          level1: "var(--joy-palette-neutral-800)",
          level2: "var(--joy-palette-neutral-700)",
          level3: "var(--joy-palette-neutral-600)",
          sidebarBg: "#121113",
        },
        text: {
          brandTitle: "#f3e8fb",
        },
      },
    },
  },
  shadow: {},
  typography: {},
  fontFamily: {
    body: "'Lato', sans-serif",
    display: "'Lato', sans-serif",
    fallback: "sans-serif",
  },
  components: {
    JoyTypography: {
      defaultProps: {
        fontFamily: "'Lato', sans-serif",
      },
    },
    JoyButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:disabled": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.08)",
            color:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.4)"
                : "rgba(0, 0, 0, 0.4)",
          },
        }),
      },
    },
  },
});

export function ThemeProvider(props: ThemeProviderProps): JSX.Element {
  return createElement(Provider, { theme, ...props });
}

export type ThemeProviderProps = {
  children: ReactNode;
};
